import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import ResponsiveDialog from './ResponsiveDialog';
import { useQuery, useMutation } from '@tanstack/react-query';
import apiService from '../services/api';
import { CreateProviderRequest, ProviderInterfaceThemeSettings } from '../types/provider';
import ProviderUserManager from './ProviderUserManager';
import ProviderAddressAutocompleteMap from './ProviderAddressAutocompleteMap';
import ProviderPlacesSection from './ProviderPlacesSection';

interface CreateProviderDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DEFAULT_INTERFACE_THEME: ProviderInterfaceThemeSettings = {
  preset: 'sherlook',
  primary: '#1976d2',
  secondary: '#dc004e',
  background: '#f4f6f8',
};

const THEME_PRESETS: Array<{ key: string; label: string; colors: ProviderInterfaceThemeSettings }> = [
  { key: 'sherlook', label: 'Sherlook', colors: { primary: '#1976d2', secondary: '#dc004e', background: '#f4f6f8' } },
  { key: 'forest', label: 'Forêt', colors: { primary: '#2e7d32', secondary: '#8bc34a', background: '#f3f8f4' } },
  { key: 'sunset', label: 'Coucher de soleil', colors: { primary: '#ef6c00', secondary: '#ad1457', background: '#fff7f1' } },
  { key: 'night', label: 'Nuit', colors: { primary: '#283593', secondary: '#00838f', background: '#eef1f8' } },
];

const CreateProviderDialog: React.FC<CreateProviderDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [createdProviderId, setCreatedProviderId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CreateProviderRequest>({
    name: '',
    type: 'hotel',
    description: '',
    website: '',
    phone: '',
    email: '',
    monthly_fee: '',
    admin_email: '',
    admin_first_name: '',
    admin_last_name: '',
    address: {
      name: '',
      address: '',
      city: '',
      region: '',
      country: 'France',
      postal_code: '',
    },
    settings: {
      interface_theme: DEFAULT_INTERFACE_THEME,
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [reassignNotice, setReassignNotice] = useState<string | null>(null);

  const { data: dbProviderTypes, isLoading: loadingProviderTypes } = useQuery({
    queryKey: ['provider-types-entity'],
    queryFn: () => apiService.getProviderTypesFromEntity(),
  });

  useEffect(() => {
    if (!open || !dbProviderTypes?.provider_types?.length) {
      return;
    }
    setFormData((prev) => {
      if (
        prev.provider_type_id &&
        dbProviderTypes.provider_types!.some((t) => t.id === prev.provider_type_id)
      ) {
        return prev;
      }
      const first = dbProviderTypes.provider_types![0];
      return { ...prev, provider_type_id: first.id, type: first.slug };
    });
  }, [open, dbProviderTypes]);

  const createProviderMutation = useMutation({
    mutationFn: (data: CreateProviderRequest) => apiService.createProvider(data),
    onSuccess: (response) => {
      setCreatedProviderId(response.id);
      if (response.admin_reassigned_from?.name) {
        setReassignNotice(
          `L’administrateur a été réaffecté depuis le partenaire « ${response.admin_reassigned_from.name} ». Pensez à lui en assigner un autre si besoin.`
        );
      } else {
        setReassignNotice(null);
      }
      setActiveTab(1); // Onglet « Lieux » puis utilisateurs
    },
    onError: (error: any) => {
      if (error.response?.data?.details) {
        setErrors(error.response.data.details);
      } else {
        setErrors({ general: error.response?.data?.error || 'Erreur lors de la création' });
      }
    },
  });

  const resetForm = () => {
    const first = dbProviderTypes?.provider_types?.[0];
    setFormData({
      name: '',
      type: first?.slug ?? 'hotel',
      provider_type_id: first?.id,
      description: '',
      website: '',
      phone: '',
      email: '',
      monthly_fee: '',
      admin_email: '',
      admin_first_name: '',
      admin_last_name: '',
      address: {
        name: '',
        address: '',
        city: '',
        region: '',
        country: 'France',
        postal_code: '',
      },
      settings: {
        interface_theme: DEFAULT_INTERFACE_THEME,
      },
    });
    setErrors({});
  };

  const interfaceTheme = ((formData.settings as Record<string, any>)?.interface_theme ||
    DEFAULT_INTERFACE_THEME) as ProviderInterfaceThemeSettings;

  const updateInterfaceTheme = (next: Partial<ProviderInterfaceThemeSettings>) => {
    setFormData((prev) => {
      const currentSettings = (prev.settings as Record<string, any>) || {};
      const currentTheme =
        (currentSettings.interface_theme as ProviderInterfaceThemeSettings) || DEFAULT_INTERFACE_THEME;

      return {
        ...prev,
        settings: {
          ...currentSettings,
          interface_theme: {
            ...currentTheme,
            ...next,
          },
        },
      };
    });
  };

  const handleClose = () => {
    resetForm();
    setActiveTab(0);
    setCreatedProviderId(null);
    setReassignNotice(null);
    onClose();
  };

  const handleFinalClose = () => {
    onSuccess();
    handleClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setReassignNotice(null);
    createProviderMutation.mutate(formData);
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('address.')) {
      const addressField = field.replace('address.', '');
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div">
          Créer un nouveau partenaire
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ajoutez un nouveau partenaire de lieu à la plateforme
        </Typography>
      </DialogTitle>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Informations générales" />
          <Tab label="Lieux" disabled={!createdProviderId} />
          <Tab label="Utilisateurs" disabled={!createdProviderId} />
        </Tabs>
      </Box>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {errors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.general}
            </Alert>
          )}

          {reassignNotice && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {reassignNotice}
            </Alert>
          )}

          {activeTab === 0 && (
            <Grid container spacing={3}>
            {/* Informations générales */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Informations générales
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom du partenaire"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={loadingProviderTypes}>
                <InputLabel>Type de partenaire (base)</InputLabel>
                <Select
                  value={formData.provider_type_id ?? ''}
                  label="Type de partenaire (base)"
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    const pt = dbProviderTypes?.provider_types?.find((t) => t.id === id);
                    setFormData((prev) => ({
                      ...prev,
                      provider_type_id: id,
                      type: pt?.slug ?? prev.type,
                    }));
                  }}
                >
                  {(dbProviderTypes?.provider_types ?? []).map((pt) => (
                    <MenuItem key={pt.id} value={pt.id}>
                      {pt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Site web"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                error={!!errors.website}
                helperText={errors.website}
                placeholder="https://example.com"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
                placeholder="01 23 45 67 89"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email de contact"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                type="email"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Abonnement mensuel (€)"
                value={formData.monthly_fee}
                onChange={(e) => handleInputChange('monthly_fee', e.target.value)}
                error={!!errors.monthly_fee}
                helperText={errors.monthly_fee}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Administrateur du partenaire
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email de l'admin"
                value={formData.admin_email}
                onChange={(e) => handleInputChange('admin_email', e.target.value)}
                error={!!errors.admin_email}
                helperText={
                  errors.admin_email ||
                  'Compte Sherlook existant accepté. S’il est déjà rattaché à un autre partenaire, il sera réaffecté ici.'
                }
                type="email"
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prénom de l'admin"
                value={formData.admin_first_name}
                onChange={(e) => handleInputChange('admin_first_name', e.target.value)}
                error={!!errors.admin_first_name}
                helperText={errors.admin_first_name}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom de l'admin"
                value={formData.admin_last_name}
                onChange={(e) => handleInputChange('admin_last_name', e.target.value)}
                error={!!errors.admin_last_name}
                helperText={errors.admin_last_name}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Adresse du partenaire
              </Typography>
              <ProviderAddressAutocompleteMap
                value={formData.address}
                onChange={(next) =>
                  setFormData((prev) => ({
                    ...prev,
                    address: next,
                  }))
                }
                logoUrl={null}
                fieldErrors={{
                  name: errors['address.name'],
                  address: errors['address.address'],
                  city: errors['address.city'],
                  region: errors['address.region'],
                  postal_code: errors['address.postal_code'],
                  country: errors['address.country'],
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Theme de couleurs
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ces couleurs seront appliquees sur l&apos;interface du dashboard partenaire.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Theme preset</InputLabel>
                <Select
                  label="Theme preset"
                  value={interfaceTheme.preset || 'custom'}
                  onChange={(e) => {
                    const preset = String(e.target.value);
                    const found = THEME_PRESETS.find((p) => p.key === preset);
                    if (found) {
                      updateInterfaceTheme({ preset: found.key, ...found.colors });
                    } else {
                      updateInterfaceTheme({ preset: 'custom' });
                    }
                  }}
                >
                  {THEME_PRESETS.map((preset) => (
                    <MenuItem key={preset.key} value={preset.key}>
                      {preset.label}
                    </MenuItem>
                  ))}
                  <MenuItem value="custom">Personnalise</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                  backgroundColor: interfaceTheme.background || DEFAULT_INTERFACE_THEME.background,
                }}
              >
                <Box display="flex" gap={1} mb={1.5}>
                  <Box sx={{ width: 24, height: 24, borderRadius: 999, bgcolor: interfaceTheme.primary }} />
                  <Box sx={{ width: 24, height: 24, borderRadius: 999, bgcolor: interfaceTheme.secondary }} />
                </Box>
                <Button size="small" variant="contained" sx={{ mr: 1 }}>
                  Primaire
                </Button>
                <Button size="small" variant="outlined">
                  Secondaire
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Couleur primaire"
                type="color"
                value={interfaceTheme.primary || DEFAULT_INTERFACE_THEME.primary}
                onChange={(e) => updateInterfaceTheme({ preset: 'custom', primary: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Couleur secondaire"
                type="color"
                value={interfaceTheme.secondary || DEFAULT_INTERFACE_THEME.secondary}
                onChange={(e) => updateInterfaceTheme({ preset: 'custom', secondary: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Fond global"
                type="color"
                value={interfaceTheme.background || DEFAULT_INTERFACE_THEME.background}
                onChange={(e) => updateInterfaceTheme({ preset: 'custom', background: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

          </Grid>
          )}

          {activeTab === 1 && createdProviderId && (
            <Box sx={{ mt: 2 }}>
              <ProviderPlacesSection
                providerId={createdProviderId}
                defaultAttributesTemplate={
                  dbProviderTypes?.provider_types?.find((t) => t.id === formData.provider_type_id)
                    ?.default_attributes || {}
                }
              />
            </Box>
          )}

          {activeTab === 2 && createdProviderId && (
            <Box sx={{ mt: 2 }}>
              <ProviderUserManager
                providerId={createdProviderId}
                providerName={formData.name}
                onUserAdded={() => {}}
                onUserRemoved={() => {}}
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={createProviderMutation.isPending}>
            Annuler
          </Button>
          
          {activeTab === 0 && (
            <Button
              type="submit"
              variant="contained"
              disabled={
                createProviderMutation.isPending ||
                loadingProviderTypes ||
                formData.provider_type_id == null
              }
              startIcon={createProviderMutation.isPending ? <CircularProgress size={20} /> : undefined}
            >
              {createProviderMutation.isPending ? 'Création...' : 'Créer le partenaire'}
            </Button>
          )}
          
          {activeTab === 1 && (
            <>
              <Button variant="outlined" onClick={handleFinalClose}>
                Terminer
              </Button>
              <Button variant="contained" onClick={() => setActiveTab(2)}>
                Continuer vers les utilisateurs
              </Button>
            </>
          )}

          {activeTab === 2 && (
            <Button variant="contained" onClick={handleFinalClose}>
              Terminer
            </Button>
          )}
        </DialogActions>
      </form>
    </ResponsiveDialog>
  );
};

export default CreateProviderDialog;

